import React, { useState, useEffect } from 'react';
import { tenantsApi, createApiPublicApi, Tenant } from '../services/api';
import { useAuth } from '../store/useStore';

// Helper type for creating/editing tenants
type TenantFormData = {
  company_name: string;
  ruc: string;
  email: string;
  environment: string;
  status: string;
  razon_social_completa?: string;
  direccion_fiscal?: string;
  ubigeo?: string;
  codigo_local_anexo?: string;
  tipo_contribuyente?: string;
  clave_sol_usuario?: string;
  clave_sol_password?: string;
  allowed_document_types: string[];
  sunat_beta_url?: string;
  sunat_prod_url?: string;
  // GRE OAuth 2.0 credentials
  gre_client_id?: string;
  gre_client_secret?: string;
  gre_email_autorizado?: string;
  // Detracción SPOT
  cuenta_banco_nacion?: string;
};

const TenantsManager: React.FC = () => {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showApiModal, setShowApiModal] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [testDocumentResult, setTestDocumentResult] = useState<string | null>(null);
  const [testLoading, setTestLoading] = useState(false);
  const [apiTab, setApiTab] = useState<'gateway' | 'public'>('public');
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [newTenant, setNewTenant] = useState<TenantFormData>({
    company_name: '',
    ruc: '',
    email: '',
    environment: 'beta',
    status: 'active',
    razon_social_completa: '',
    direccion_fiscal: '',
    ubigeo: '',
    codigo_local_anexo: '0000',
    tipo_contribuyente: 'persona_juridica',
    clave_sol_usuario: '',
    clave_sol_password: '',
    allowed_document_types: ['01', '03'], // Factura y Boleta by default
    sunat_beta_url: 'https://e-beta.sunat.gob.pe/ol-ti-itcpfegem-beta/billService',
    sunat_prod_url: 'https://e-factura.sunat.gob.pe/ol-ti-itcpfegem/billService',
    // GRE OAuth 2.0 credentials
    gre_client_id: '',
    gre_client_secret: '',
    gre_email_autorizado: '',
    // Detracción SPOT
    cuenta_banco_nacion: '',
  });
  const [editTenant, setEditTenant] = useState<TenantFormData>({
    company_name: '',
    ruc: '',
    email: '',
    environment: 'beta',
    status: 'active',
    razon_social_completa: '',
    direccion_fiscal: '',
    ubigeo: '',
    codigo_local_anexo: '0000',
    tipo_contribuyente: 'persona_juridica',
    clave_sol_usuario: '',
    clave_sol_password: '',
    allowed_document_types: ['01', '03'],
    sunat_beta_url: '',
    sunat_prod_url: '',
    // GRE OAuth 2.0 credentials
    gre_client_id: '',
    gre_client_secret: '',
    gre_email_autorizado: '',
    // Detracción SPOT
    cuenta_banco_nacion: '',
  });

  const { user } = useAuth();
  const isRegularUser = user && user.role !== 'super_admin';
  const userTenantId = user?.tenant_id;

  useEffect(() => {
    fetchTenants();
  }, []);

  const fetchTenants = async () => {
    try {
      setLoading(true);
      const response = await tenantsApi.getAll();
      setTenants(response.data.tenants || []);
      setError(null);
    } catch (err: any) {
      if (err.response?.status === 401) {
        setError('Authentication required. Please login.');
      } else if (err.response?.status === 403) {
        setError('Access denied. Insufficient permissions.');
      } else {
        setError(err.response?.data?.detail || err.message || 'Failed to fetch tenants');
      }
      console.error('Tenants fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const createTenant = async () => {
    try {
      await tenantsApi.create(newTenant as Partial<Tenant>);
      setNewTenant({
        company_name: '',
        ruc: '',
        email: '',
        environment: 'beta' as const,
        status: 'active' as const,
        razon_social_completa: '',
        direccion_fiscal: '',
        ubigeo: '',
        tipo_contribuyente: 'persona_juridica' as const,
        clave_sol_usuario: '',
        clave_sol_password: '',
        allowed_document_types: ['01', '03'],
        sunat_beta_url: 'https://e-beta.sunat.gob.pe/ol-ti-itcpfegem-beta/billService',
        sunat_prod_url: 'https://e-factura.sunat.gob.pe/ol-ti-itcpfegem/billService',
      });
      setShowCreateModal(false);
      fetchTenants(); // Refresh the list
    } catch (err: any) {
      setError(err.message || 'Failed to create tenant');
    }
  };

  const handleViewTenant = async (tenant: Tenant) => {
    setSelectedTenant(tenant);
    setShowViewModal(true);
  };

  const handleEditTenant = (tenant: Tenant) => {
    setSelectedTenant(tenant);
    setEditTenant({
      company_name: tenant.company_name,
      ruc: tenant.ruc,
      email: tenant.email,
      environment: tenant.environment,
      status: tenant.status || 'active',
      razon_social_completa: tenant.razon_social_completa || '',
      direccion_fiscal: tenant.direccion_fiscal || '',
      ubigeo: tenant.ubigeo || '',
      codigo_local_anexo: tenant.codigo_local_anexo || '0000',
      tipo_contribuyente: tenant.tipo_contribuyente || 'persona_juridica',
      clave_sol_usuario: tenant.clave_sol_usuario || '',
      clave_sol_password: '', // Don't show existing password - leave empty
      allowed_document_types: tenant.allowed_document_types || ['01', '03'],
      sunat_beta_url: tenant.sunat_beta_url || 'https://e-beta.sunat.gob.pe/ol-ti-itcpfegem-beta/billService',
      sunat_prod_url: tenant.sunat_prod_url || 'https://e-factura.sunat.gob.pe/ol-ti-itcpfegem/billService',
      // GRE OAuth 2.0 credentials
      gre_client_id: tenant.gre_client_id || '',
      gre_client_secret: '', // Don't show existing secret
      gre_email_autorizado: tenant.gre_email_autorizado || '',
      // Detracción SPOT
      cuenta_banco_nacion: tenant.cuenta_banco_nacion || '',
    });
    setShowEditModal(true);
  };

  const updateTenant = async () => {
    if (!selectedTenant) return;
    try {
      console.log('Updating tenant with ID:', selectedTenant.id);
      console.log('Update data:', editTenant);

      // Filter out empty password fields to avoid validation errors
      const updateData = { ...editTenant };
      if (!updateData.clave_sol_password || updateData.clave_sol_password === '') {
        delete updateData.clave_sol_password;
      }
      if (!updateData.gre_client_secret || updateData.gre_client_secret === '') {
        delete updateData.gre_client_secret;
      }

      console.log('Filtered update data:', updateData);

      const response = await tenantsApi.update(selectedTenant.id, updateData as Partial<Tenant>);
      console.log('Update response:', response);

      setShowEditModal(false);
      setSelectedTenant(null);
      await fetchTenants(); // Refresh the list
      setError(null); // Clear any previous errors
    } catch (err: any) {
      console.error('Update tenant error:', err);
      setError(err.response?.data?.message || err.message || 'Failed to update tenant');
    }
  };

  const handleDeleteTenant = (tenant: Tenant) => {
    setSelectedTenant(tenant);
    setShowDeleteModal(true);
  };

  const handleShowApi = (tenant: Tenant) => {
    setSelectedTenant(tenant);
    setShowApiModal(true);
    setTestDocumentResult(null);
  };

  // Helper to copy text to clipboard
  const copyToClipboard = async (text: string, fieldName: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Get API URLs based on environment
  const getApiUrls = () => {
    const isProduction = window.location.hostname !== 'localhost';
    const baseHost = isProduction ? window.location.origin : 'http://localhost';
    return {
      gateway: `${baseHost}:8080/api/v1`,
      public: `${baseHost}:8010/v1`,
      swagger: `${baseHost}:8010/docs`
    };
  };

  const generateTestDocument = async (tenantId: string) => {
    try {
      setTestLoading(true);
      setTestDocumentResult(null);

      // Check if tenant has API Key
      const apiKey = selectedTenant?.api_key;
      if (!apiKey) {
        setTestDocumentResult(`❌ Error: No API Key configured for this tenant.\nGo to tenant settings to generate an API Key first.`);
        return;
      }

      // Create API Public client with tenant's API Key
      const apiPublic = createApiPublicApi(apiKey);

      // Test document using simplified api-public format
      const testInvoice = {
        document_type: "01", // Factura
        currency: "PEN",
        customer: {
          document_type: "6", // RUC
          document_number: "20123456789",
          name: "Cliente Test SAC",
          address: "Jr. Test 123, Lima"
        },
        items: [{
          description: "Producto de prueba",
          quantity: 1,
          unit_price: 118.00, // Precio con IGV incluido
          unit_code: "NIU"
        }],
        notes: "Documento de prueba generado desde Dashboard",
        auto_submit: false // No enviar automaticamente a SUNAT
      };

      const response = await apiPublic.createDocument(testInvoice);
      const result = response.data;

      setTestDocumentResult(
        `✅ Document created via API Public!\n` +
        `Document ID: ${result.id}\n` +
        `Number: ${result.document_number || 'Pending'}\n` +
        `Status: ${result.status}\n` +
        `Total: ${result.currency} ${result.total_amount?.toFixed(2) || '0.00'}`
      );
    } catch (error: any) {
      if (error.response?.data) {
        const errorData = error.response.data;
        const errorMsg = errorData.detail || errorData.message || JSON.stringify(errorData, null, 2);
        setTestDocumentResult(`❌ Error creating document:\n${errorMsg}`);
      } else {
        setTestDocumentResult(`❌ Network error: ${error.message}`);
      }
    } finally {
      setTestLoading(false);
    }
  };

  const submitToSunat = async (tenantId: string) => {
    try {
      setTestLoading(true);
      setTestDocumentResult(null);

      // Check if tenant has API Key
      const apiKey = selectedTenant?.api_key;
      if (!apiKey) {
        setTestDocumentResult(`❌ Error: No API Key configured for this tenant.\nGo to tenant settings to generate an API Key first.`);
        return;
      }

      // Create API Public client with tenant's API Key
      const apiPublic = createApiPublicApi(apiKey);

      // First, get pending documents
      const listResponse = await apiPublic.listDocuments({ status: 'draft', limit: 10 });
      const pendingDocs = listResponse.data.documents || [];

      if (pendingDocs.length === 0) {
        setTestDocumentResult(`ℹ️ No pending documents to submit.\nCreate a test document first.`);
        return;
      }

      // Submit each pending document
      let submitted = 0;
      let success = 0;
      let errors = 0;
      const results: string[] = [];

      for (const doc of pendingDocs) {
        try {
          submitted++;
          await apiPublic.submitDocument(doc.id);
          success++;
          results.push(`✅ ${doc.document_number || doc.id}: Submitted`);
        } catch (err: any) {
          errors++;
          const errMsg = err.response?.data?.detail || err.message;
          results.push(`❌ ${doc.document_number || doc.id}: ${errMsg}`);
        }
      }

      setTestDocumentResult(
        `📤 SUNAT Submission via API Public\n` +
        `Processed: ${submitted} documents\n` +
        `Success: ${success}\n` +
        `Errors: ${errors}\n\n` +
        `Details:\n${results.join('\n')}`
      );
    } catch (error: any) {
      if (error.response?.data) {
        const errorData = error.response.data;
        const errorMsg = errorData.detail || errorData.message || JSON.stringify(errorData, null, 2);
        setTestDocumentResult(`❌ Error submitting to SUNAT:\n${errorMsg}`);
      } else {
        setTestDocumentResult(`❌ Network error: ${error.message}`);
      }
    } finally {
      setTestLoading(false);
    }
  };

  const deleteTenant = async () => {
    if (!selectedTenant) return;
    try {
      await tenantsApi.delete(selectedTenant.id);
      setShowDeleteModal(false);
      setSelectedTenant(null);
      fetchTenants(); // Refresh the list
    } catch (err: any) {
      setError(err.message || 'Failed to delete tenant');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-red-100 text-red-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getEnvironmentColor = (env: string) => {
    switch (env?.toLowerCase()) {
      case 'production':
        return 'bg-red-100 text-red-800';
      case 'beta':
        return 'bg-yellow-100 text-yellow-800';
      case 'demo':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Loading tenants...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Tenants Management</h2>
          <p className="mt-1 text-sm text-gray-600">
            {isRegularUser ? 'Manage your tenant information' : 'Manage platform tenants'}
          </p>
          {user && (
            <div className="mt-2 text-sm text-blue-600">
              Logged in as: {user.username} ({user.role})
              {userTenantId && ` - Tenant: ${userTenantId.slice(0, 8)}...`}
            </div>
          )}
        </div>
        {!isRegularUser && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            ➕ Create Tenant
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="text-red-400">⚠️</div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <p className="mt-1 text-sm text-red-700">{error}</p>
              <button
                onClick={fetchTenants}
                className="mt-2 text-sm text-red-600 hover:text-red-500 underline"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <span className="text-2xl">🏢</span>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Total Tenants</p>
              <p className="text-2xl font-bold text-gray-900">{tenants.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <span className="text-2xl">✅</span>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Active</p>
              <p className="text-2xl font-bold text-green-600">
                {tenants.filter(t => t.status?.toLowerCase() === 'active').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <span className="text-2xl">🚀</span>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Production</p>
              <p className="text-2xl font-bold text-red-600">
                {tenants.filter(t => t.environment?.toLowerCase() === 'production').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <span className="text-2xl">🔐</span>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">With Certificates</p>
              <p className="text-2xl font-bold text-blue-600">
                {tenants.filter(t => t.has_digital_certificate).length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Tenants Table */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">All Tenants</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Company
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  RUC
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contributor Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Environment
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Certificate
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {tenants.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-8 text-center text-gray-500">
                    <div className="text-4xl mb-2">🏢</div>
                    <p>No tenants found</p>
                    <p className="text-sm">Create your first tenant to get started</p>
                  </td>
                </tr>
              ) : (
                tenants.map((tenant) => (
                  <tr key={tenant.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{tenant.company_name}</p>
                        <p className="text-sm text-gray-500">{tenant.email}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {tenant.ruc}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span className="capitalize">
                        {tenant.tipo_contribuyente?.replace('_', ' ') || 'N/A'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getEnvironmentColor(tenant.environment)}`}>
                        {tenant.environment}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(tenant.status)}`}>
                        {tenant.status || 'active'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {tenant.has_digital_certificate ? (
                        <div className="flex items-center">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            🔐 Active
                          </span>
                          {tenant.certificate_ruc && tenant.certificate_ruc !== tenant.ruc && (
                            <span className="ml-1 text-orange-500" title="Certificate RUC differs from tenant RUC">
                              ⚠️
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                          ❌ None
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => handleViewTenant(tenant)}
                        className="text-blue-600 hover:text-blue-900 mr-3"
                      >
                        View
                      </button>
                      <button
                        onClick={() => handleShowApi(tenant)}
                        className="text-green-600 hover:text-green-900 mr-3"
                      >
                        🚀 API
                      </button>
                      {(isRegularUser && userTenantId === tenant.id) || !isRegularUser ? (
                        <button
                          onClick={() => handleEditTenant(tenant)}
                          className="text-yellow-600 hover:text-yellow-900 mr-3"
                        >
                          Edit
                        </button>
                      ) : null}
                      {!isRegularUser && (
                        <button
                          onClick={() => handleDeleteTenant(tenant)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create Tenant Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-6 border max-w-4xl shadow-lg rounded-md bg-white max-h-screen overflow-y-auto">
            <div className="">
              <h3 className="text-lg font-medium text-gray-900 mb-6">📝 Create New SUNAT Tenant</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Basic Information */}
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-700 border-b pb-2">📋 Basic Information</h4>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Company Name</label>
                    <input
                      type="text"
                      value={newTenant.company_name}
                      onChange={(e) => setNewTenant({ ...newTenant, company_name: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter company name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Full Legal Name (Razón Social)</label>
                    <input
                      type="text"
                      value={newTenant.razon_social_completa}
                      onChange={(e) => setNewTenant({ ...newTenant, razon_social_completa: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Full legal company name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">RUC</label>
                    <input
                      type="text"
                      value={newTenant.ruc}
                      onChange={(e) => setNewTenant({ ...newTenant, ruc: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="20123456789"
                      maxLength={11}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Email</label>
                    <input
                      type="email"
                      value={newTenant.email}
                      onChange={(e) => setNewTenant({ ...newTenant, email: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="admin@company.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Contributor Type</label>
                    <select
                      value={newTenant.tipo_contribuyente}
                      onChange={(e) => setNewTenant({ ...newTenant, tipo_contribuyente: e.target.value as 'persona_natural' | 'persona_juridica' })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="persona_juridica">Legal Entity (Persona Jurídica)</option>
                      <option value="persona_natural">Natural Person (Persona Natural)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Status</label>
                    <select
                      value={newTenant.status}
                      onChange={(e) => setNewTenant({ ...newTenant, status: e.target.value as 'active' | 'inactive' | 'suspended' })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="suspended">Suspended</option>
                    </select>
                  </div>
                </div>

                {/* SUNAT Configuration */}
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-700 border-b pb-2">🏛️ SUNAT Configuration</h4>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Fiscal Address</label>
                    <textarea
                      value={newTenant.direccion_fiscal}
                      onChange={(e) => setNewTenant({ ...newTenant, direccion_fiscal: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Complete fiscal address"
                      rows={2}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Ubigeo</label>
                    <input
                      type="text"
                      value={newTenant.ubigeo}
                      onChange={(e) => setNewTenant({ ...newTenant, ubigeo: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="150101"
                      maxLength={6}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Clave SOL User</label>
                    <input
                      type="text"
                      value={newTenant.clave_sol_usuario}
                      onChange={(e) => setNewTenant({ ...newTenant, clave_sol_usuario: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="SUNAT username"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Clave SOL Password</label>
                    <input
                      type="password"
                      value={newTenant.clave_sol_password}
                      onChange={(e) => setNewTenant({ ...newTenant, clave_sol_password: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="SUNAT password"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Environment</label>
                    <select
                      value={newTenant.environment}
                      onChange={(e) => setNewTenant({ ...newTenant, environment: e.target.value as 'beta' | 'production' })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="beta">Beta (Testing)</option>
                      <option value="production">Production</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Allowed Document Types</label>
                    <div className="mt-1 space-y-2">
                      {[
                        { value: '01', label: 'Invoice (Factura)' },
                        { value: '03', label: 'Boleta de Venta' },
                        { value: '07', label: 'Credit Note (Nota de Crédito)' },
                        { value: '08', label: 'Debit Note (Nota de Débito)' },
                        { value: '09', label: 'Dispatch Guide (Guía de Remisión)' },
                      ].map((docType) => (
                        <label key={docType.value} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={newTenant.allowed_document_types?.includes(docType.value) || false}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setNewTenant({
                                  ...newTenant,
                                  allowed_document_types: [...(newTenant.allowed_document_types || []), docType.value]
                                });
                              } else {
                                setNewTenant({
                                  ...newTenant,
                                  allowed_document_types: (newTenant.allowed_document_types || []).filter(t => t !== docType.value)
                                });
                              }
                            }}
                            className="mr-2"
                          />
                          <span className="text-sm">{docType.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* GRE OAuth 2.0 Configuration - Full width section */}
              <div className="mt-6 pt-6 border-t">
                <h4 className="font-medium text-gray-700 border-b pb-2 mb-4">🚚 GRE Configuration - Guías de Remisión Electrónica (OAuth 2.0)</h4>

                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-green-800">
                    <strong>ℹ️ Nota:</strong> Las credenciales Usuario SOL y Contraseña SOL son las mismas configuradas arriba (para Facturas/Boletas).
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Client ID (API SUNAT)</label>
                    <input
                      type="text"
                      value={newTenant.gre_client_id}
                      onChange={(e) => setNewTenant({ ...newTenant, gre_client_id: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-green-500 focus:border-green-500"
                      placeholder="Ej: 702fec90-23c0-4842-a012-53059fd1572b"
                    />
                    <p className="mt-1 text-xs text-gray-500">UUID proporcionado por SUNAT</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Client Secret (Clave API)</label>
                    <input
                      type="password"
                      value={newTenant.gre_client_secret}
                      onChange={(e) => setNewTenant({ ...newTenant, gre_client_secret: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-green-500 focus:border-green-500"
                      placeholder="Clave secreta en base64"
                    />
                    <p className="mt-1 text-xs text-gray-500">Clave API en formato base64</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Email Autorizado (GRE)</label>
                    <input
                      type="email"
                      value={newTenant.gre_email_autorizado}
                      onChange={(e) => setNewTenant({ ...newTenant, gre_email_autorizado: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-green-500 focus:border-green-500"
                      placeholder="facturacion@empresa.com"
                    />
                    <p className="mt-1 text-xs text-gray-500">Para notificaciones de GRE</p>
                  </div>
                </div>
              </div>

              {/* Detracción SPOT - Full width section */}
              <div className="mt-6 pt-6 border-t">
                <h4 className="font-medium text-gray-700 border-b pb-2 mb-4">💳 Detracción SPOT - Banco de la Nación</h4>

                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-purple-800">
                    <strong>ℹ️ Sistema de Pago de Obligaciones Tributarias (SPOT):</strong> Cuenta del Banco de la Nación para el depósito de detracciones en facturas sujetas a este régimen.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Cuenta del Banco de la Nación (11 dígitos)</label>
                  <input
                    type="text"
                    value={newTenant.cuenta_banco_nacion}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, ''); // Only numbers
                      setNewTenant({ ...newTenant, cuenta_banco_nacion: value });
                    }}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                    placeholder="12345678901"
                    maxLength={11}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Cuenta de 11 dígitos del Banco de la Nación. Se auto-cargará en facturas con detracción.
                  </p>
                  {newTenant.cuenta_banco_nacion && newTenant.cuenta_banco_nacion.length !== 11 && (
                    <p className="mt-1 text-xs text-red-600">
                      ⚠️ La cuenta debe tener exactamente 11 dígitos
                    </p>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-end pt-6 space-x-3 border-t mt-6">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={createTenant}
                  disabled={!newTenant.company_name || !newTenant.ruc || !newTenant.email || !newTenant.razon_social_completa}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                >
                  ✅ Create SUNAT Tenant
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Tenant Modal */}
      {showViewModal && selectedTenant && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-6 border max-w-4xl shadow-lg rounded-md bg-white max-h-screen overflow-y-auto">
            <div className="">
              <h3 className="text-lg font-medium text-gray-900 mb-6">📋 SUNAT Tenant Details</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Basic Information */}
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-700 border-b pb-2">📋 Basic Information</h4>

                  <div>
                    <label className="block text-sm font-medium text-gray-500">Company Name</label>
                    <p className="mt-1 text-sm text-gray-900 bg-gray-50 p-2 rounded">{selectedTenant.company_name}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-500">Full Legal Name (Razón Social)</label>
                    <p className="mt-1 text-sm text-gray-900 bg-gray-50 p-2 rounded">{selectedTenant.razon_social_completa || 'N/A'}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-500">RUC</label>
                    <p className="mt-1 text-sm text-gray-900 bg-gray-50 p-2 rounded font-mono">{selectedTenant.ruc}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-500">Email</label>
                    <p className="mt-1 text-sm text-gray-900 bg-gray-50 p-2 rounded">{selectedTenant.email}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-500">Contributor Type</label>
                    <p className="mt-1 text-sm text-gray-900 bg-gray-50 p-2 rounded capitalize">
                      {selectedTenant.tipo_contribuyente?.replace('_', ' ') || 'N/A'}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-500">Environment</label>
                    <div className="mt-1">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getEnvironmentColor(selectedTenant.environment)}`}>
                        {selectedTenant.environment}
                      </span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-500">Status</label>
                    <div className="mt-1">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(selectedTenant.status)}`}>
                        {selectedTenant.status || 'active'}
                      </span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-500">Created Date</label>
                    <p className="mt-1 text-sm text-gray-900 bg-gray-50 p-2 rounded">
                      {selectedTenant.created_at ? new Date(selectedTenant.created_at).toLocaleString() : 'N/A'}
                    </p>
                  </div>
                </div>

                {/* SUNAT Configuration */}
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-700 border-b pb-2">🏛️ SUNAT Configuration</h4>

                  <div>
                    <label className="block text-sm font-medium text-gray-500">Fiscal Address</label>
                    <p className="mt-1 text-sm text-gray-900 bg-gray-50 p-2 rounded">
                      {selectedTenant.direccion_fiscal || 'N/A'}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-500">Ubigeo</label>
                    <p className="mt-1 text-sm text-gray-900 bg-gray-50 p-2 rounded font-mono">
                      {selectedTenant.ubigeo || 'N/A'}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-500">Clave SOL User</label>
                    <p className="mt-1 text-sm text-gray-900 bg-gray-50 p-2 rounded font-mono">
                      {selectedTenant.clave_sol_usuario || 'N/A'}
                    </p>
                    {selectedTenant.clave_sol_password && isRegularUser && (
                      <div className="mt-2">
                        <label className="block text-sm font-medium text-gray-500">Clave SOL Password</label>
                        <p className="mt-1 text-sm text-gray-500 bg-gray-50 p-2 rounded italic">
                          [Protected - Only visible to administrators]
                        </p>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-500">Digital Certificate Status</label>
                    <div className="mt-1 bg-gray-50 p-3 rounded">
                      {selectedTenant.has_digital_certificate ? (
                        <div className="space-y-2">
                          <div className="flex items-center space-x-2">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              ✅ Certificate Assigned
                            </span>
                            {selectedTenant.certificate_status && (
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                selectedTenant.certificate_status === 'active' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                              }`}>
                                {selectedTenant.certificate_status}
                              </span>
                            )}
                          </div>

                          {selectedTenant.certificate_id && (
                            <div className="text-xs">
                              <span className="text-gray-500">Certificate ID:</span>
                              <span className="ml-1 font-mono text-gray-700">{selectedTenant.certificate_id.slice(0, 8)}...</span>
                            </div>
                          )}

                          {selectedTenant.certificate_ruc && (
                            <div className="text-xs">
                              <span className="text-gray-500">Certificate RUC:</span>
                              <span className="ml-1 font-mono text-gray-700">{selectedTenant.certificate_ruc}</span>
                              {selectedTenant.certificate_ruc === selectedTenant.ruc ? (
                                <span className="ml-2 text-green-600">✓ Matches Tenant RUC</span>
                              ) : (
                                <span className="ml-2 text-orange-600">⚠ Different from Tenant RUC</span>
                              )}
                            </div>
                          )}

                          {selectedTenant.certificate_uploaded_at && (
                            <div className="text-xs">
                              <span className="text-gray-500">Uploaded:</span>
                              <span className="ml-1 text-gray-700">
                                {new Date(selectedTenant.certificate_uploaded_at).toLocaleString()}
                              </span>
                            </div>
                          )}

                          <div className="text-xs text-green-600 font-medium">
                            🚀 Auto-assigned from certificate upload
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-2">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                            ❌ No certificate assigned
                          </span>
                          <p className="text-xs text-gray-500 mt-1">
                            Upload a certificate with matching RUC for automatic assignment
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-500">Allowed Document Types</label>
                    <div className="mt-1 bg-gray-50 p-2 rounded">
                      {selectedTenant.allowed_document_types && selectedTenant.allowed_document_types.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {selectedTenant.allowed_document_types.map((type) => {
                            const labels: Record<string, string> = {
                              '01': 'Invoice',
                              '03': 'Boleta',
                              '07': 'Credit Note',
                              '08': 'Debit Note',
                              '09': 'Dispatch Guide'
                            };
                            return (
                              <span key={type} className="inline-flex items-center px-2 py-1 rounded text-xs bg-blue-100 text-blue-800">
                                {type}: {labels[type] || 'Unknown'}
                              </span>
                            );
                          })}
                        </div>
                      ) : (
                        <span className="text-gray-500 text-sm">No document types configured</span>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-500">SUNAT URLs</label>
                    <div className="mt-1 space-y-2">
                      <div className="bg-yellow-50 p-2 rounded">
                        <p className="text-xs text-gray-500">Beta:</p>
                        <p className="text-xs text-gray-900 font-mono break-all">
                          {selectedTenant.sunat_beta_url || 'N/A'}
                        </p>
                      </div>
                      <div className="bg-red-50 p-2 rounded">
                        <p className="text-xs text-gray-500">Production:</p>
                        <p className="text-xs text-gray-900 font-mono break-all">
                          {selectedTenant.sunat_prod_url || 'N/A'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* GRE OAuth 2.0 Configuration */}
              <div className="mt-6 pt-6 border-t">
                <h4 className="font-medium text-gray-700 border-b pb-2 mb-4">🚚 GRE Configuration - Guías de Remisión Electrónica (OAuth 2.0)</h4>

                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-green-800">
                    <strong>ℹ️ Nota:</strong> Las credenciales Usuario SOL y Contraseña SOL son las mismas configuradas arriba (para Facturas/Boletas).
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Client ID (API SUNAT)</label>
                    <p className="mt-1 text-sm text-gray-900 bg-gray-50 p-2 rounded font-mono break-all">
                      {selectedTenant.gre_client_id || 'No configurado'}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-500">Client Secret (Clave API)</label>
                    <p className="mt-1 text-sm text-gray-500 bg-gray-50 p-2 rounded italic">
                      {selectedTenant.gre_client_secret ? '[Protected - Encrypted]' : 'No configurado'}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-500">Email Autorizado (GRE)</label>
                    <p className="mt-1 text-sm text-gray-900 bg-gray-50 p-2 rounded">
                      {selectedTenant.gre_email_autorizado || 'No configurado'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Detracción SPOT Configuration */}
              <div className="mt-6 pt-6 border-t">
                <h4 className="font-medium text-gray-700 border-b pb-2 mb-4">💳 Detracción SPOT - Banco de la Nación</h4>

                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-purple-800">
                    <strong>ℹ️ Sistema de Pago de Obligaciones Tributarias (SPOT):</strong> Cuenta del Banco de la Nación para el depósito de detracciones.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-500">Cuenta del Banco de la Nación</label>
                  <p className="mt-1 text-sm text-gray-900 bg-gray-50 p-2 rounded font-mono">
                    {selectedTenant.cuenta_banco_nacion || 'No configurado'}
                  </p>
                  {selectedTenant.cuenta_banco_nacion && (
                    <div className="mt-2">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        ✓ Cuenta configurada - Se auto-cargará en facturas con detracción
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-end pt-6 border-t mt-6">
                <button
                  onClick={() => setShowViewModal(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Tenant Modal */}
      {showEditModal && selectedTenant && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-6 border max-w-4xl shadow-lg rounded-md bg-white max-h-screen overflow-y-auto">
            <div className="">
              <h3 className="text-lg font-medium text-gray-900 mb-6">✏️ Edit SUNAT Tenant</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Basic Information */}
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-700 border-b pb-2">📋 Basic Information</h4>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Company Name</label>
                    <input
                      type="text"
                      value={editTenant.company_name}
                      onChange={(e) => setEditTenant({ ...editTenant, company_name: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter company name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Full Legal Name (Razón Social)</label>
                    <input
                      type="text"
                      value={editTenant.razon_social_completa}
                      onChange={(e) => setEditTenant({ ...editTenant, razon_social_completa: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Full legal company name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">RUC</label>
                    <input
                      type="text"
                      value={editTenant.ruc}
                      onChange={(e) => setEditTenant({ ...editTenant, ruc: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="20123456789"
                      maxLength={11}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Email</label>
                    <input
                      type="email"
                      value={editTenant.email}
                      onChange={(e) => setEditTenant({ ...editTenant, email: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="admin@company.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Contributor Type</label>
                    <select
                      value={editTenant.tipo_contribuyente}
                      onChange={(e) => setEditTenant({ ...editTenant, tipo_contribuyente: e.target.value as 'persona_natural' | 'persona_juridica' })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="persona_juridica">Legal Entity (Persona Jurídica)</option>
                      <option value="persona_natural">Natural Person (Persona Natural)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Status</label>
                    <select
                      value={editTenant.status}
                      onChange={(e) => setEditTenant({ ...editTenant, status: e.target.value as 'active' | 'inactive' | 'suspended' })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="suspended">Suspended</option>
                    </select>
                  </div>
                </div>

                {/* SUNAT Configuration */}
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-700 border-b pb-2">🏛️ SUNAT Configuration</h4>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Fiscal Address</label>
                    <textarea
                      value={editTenant.direccion_fiscal}
                      onChange={(e) => setEditTenant({ ...editTenant, direccion_fiscal: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Complete fiscal address"
                      rows={2}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Ubigeo</label>
                    <input
                      type="text"
                      value={editTenant.ubigeo}
                      onChange={(e) => setEditTenant({ ...editTenant, ubigeo: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="150101"
                      maxLength={6}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Código de Local Anexo</label>
                    <input
                      type="text"
                      value={editTenant.codigo_local_anexo}
                      onChange={(e) => setEditTenant({ ...editTenant, codigo_local_anexo: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="0000"
                      maxLength={4}
                    />
                    <p className="mt-1 text-sm text-gray-500">Código de establecimiento SUNAT (0000 para principal)</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Clave SOL User</label>
                    <input
                      type="text"
                      value={editTenant.clave_sol_usuario}
                      onChange={(e) => setEditTenant({ ...editTenant, clave_sol_usuario: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="SUNAT username"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Clave SOL Password</label>
                    <input
                      type="password"
                      value={editTenant.clave_sol_password}
                      onChange={(e) => setEditTenant({ ...editTenant, clave_sol_password: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder={isRegularUser ? "Leave empty to keep current" : "Update password if needed"}
                    />
                    {isRegularUser && (
                      <p className="mt-1 text-xs text-gray-500">
                        Password is protected and will be filtered in responses for security.
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Environment</label>
                    <select
                      value={editTenant.environment}
                      onChange={(e) => setEditTenant({ ...editTenant, environment: e.target.value as 'beta' | 'production' })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="beta">Beta (Testing)</option>
                      <option value="production">Production</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Allowed Document Types</label>
                    <div className="mt-1 space-y-2">
                      {[
                        { value: '01', label: 'Invoice (Factura)' },
                        { value: '03', label: 'Boleta de Venta' },
                        { value: '07', label: 'Credit Note (Nota de Crédito)' },
                        { value: '08', label: 'Debit Note (Nota de Débito)' },
                        { value: '09', label: 'Dispatch Guide (Guía de Remisión)' },
                      ].map((docType) => (
                        <label key={docType.value} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={editTenant.allowed_document_types?.includes(docType.value) || false}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setEditTenant({
                                  ...editTenant,
                                  allowed_document_types: [...(editTenant.allowed_document_types || []), docType.value]
                                });
                              } else {
                                setEditTenant({
                                  ...editTenant,
                                  allowed_document_types: (editTenant.allowed_document_types || []).filter(t => t !== docType.value)
                                });
                              }
                            }}
                            className="mr-2"
                          />
                          <span className="text-sm">{docType.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* GRE OAuth 2.0 Configuration - Full width section */}
              <div className="mt-6 pt-6 border-t">
                <h4 className="font-medium text-gray-700 border-b pb-2 mb-4">🚚 GRE Configuration - Guías de Remisión Electrónica (OAuth 2.0)</h4>

                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-green-800">
                    <strong>ℹ️ Nota:</strong> Las credenciales Usuario SOL y Contraseña SOL son las mismas configuradas arriba (para Facturas/Boletas).
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Client ID (API SUNAT)</label>
                    <input
                      type="text"
                      value={editTenant.gre_client_id}
                      onChange={(e) => setEditTenant({ ...editTenant, gre_client_id: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-green-500 focus:border-green-500"
                      placeholder="Ej: 702fec90-23c0-4842-a012-53059fd1572b"
                    />
                    <p className="mt-1 text-xs text-gray-500">UUID proporcionado por SUNAT</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Client Secret (Clave API)</label>
                    <input
                      type="password"
                      value={editTenant.gre_client_secret}
                      onChange={(e) => setEditTenant({ ...editTenant, gre_client_secret: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-green-500 focus:border-green-500"
                      placeholder="Dejar vacío para no cambiar"
                    />
                    <p className="mt-1 text-xs text-gray-500">Clave API en formato base64</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Email Autorizado (GRE)</label>
                    <input
                      type="email"
                      value={editTenant.gre_email_autorizado}
                      onChange={(e) => setEditTenant({ ...editTenant, gre_email_autorizado: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-green-500 focus:border-green-500"
                      placeholder="facturacion@empresa.com"
                    />
                    <p className="mt-1 text-xs text-gray-500">Para notificaciones de GRE</p>
                  </div>
                </div>
              </div>

              {/* Detracción SPOT - Full width section */}
              <div className="mt-6 pt-6 border-t">
                <h4 className="font-medium text-gray-700 border-b pb-2 mb-4">💳 Detracción SPOT - Banco de la Nación</h4>

                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-purple-800">
                    <strong>ℹ️ Sistema de Pago de Obligaciones Tributarias (SPOT):</strong> Cuenta del Banco de la Nación para el depósito de detracciones en facturas sujetas a este régimen.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Cuenta del Banco de la Nación (11 dígitos)</label>
                  <input
                    type="text"
                    value={editTenant.cuenta_banco_nacion}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, ''); // Only numbers
                      setEditTenant({ ...editTenant, cuenta_banco_nacion: value });
                    }}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                    placeholder="12345678901"
                    maxLength={11}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Cuenta de 11 dígitos del Banco de la Nación. Se auto-cargará en facturas con detracción.
                  </p>
                  {editTenant.cuenta_banco_nacion && editTenant.cuenta_banco_nacion.length !== 11 && (
                    <p className="mt-1 text-xs text-red-600">
                      ⚠️ La cuenta debe tener exactamente 11 dígitos
                    </p>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-end pt-6 space-x-3 border-t mt-6">
                <button
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={updateTenant}
                  disabled={!editTenant.company_name || !editTenant.ruc || !editTenant.email}
                  className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                >
                  💾 Update SUNAT Tenant
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Tenant Modal */}
      {showDeleteModal && selectedTenant && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full">
                <span className="text-red-600 text-2xl">⚠️</span>
              </div>

              <h3 className="text-lg font-medium text-gray-900 text-center mt-4 mb-4">
                Delete Tenant
              </h3>

              <p className="text-sm text-gray-500 text-center mb-4">
                Are you sure you want to delete "{selectedTenant.company_name}"?
                This action cannot be undone.
              </p>

              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 mb-4">
                <p className="text-sm text-yellow-800">
                  <strong>Warning:</strong> This will permanently delete all data associated with this tenant.
                </p>
              </div>

              <div className="flex items-center justify-end space-x-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={deleteTenant}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                >
                  Delete Tenant
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* API Usage Modal - Updated with tabs and api-public */}
      {showApiModal && selectedTenant && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-5 mx-auto p-6 border max-w-5xl shadow-lg rounded-md bg-white max-h-[95vh] overflow-y-auto">
            <div className="">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">🚀 API Integration</h3>
                  <p className="text-sm text-gray-600">Tenant: {selectedTenant.company_name} ({selectedTenant.ruc})</p>
                </div>
                <button
                  onClick={() => setShowApiModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>

              {/* Tabs */}
              <div className="flex border-b mb-6">
                <button
                  onClick={() => setApiTab('public')}
                  className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
                    apiTab === 'public'
                      ? 'border-green-500 text-green-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  🔑 API Public (API Key)
                </button>
                <button
                  onClick={() => setApiTab('gateway')}
                  className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
                    apiTab === 'gateway'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  🔐 API Gateway (JWT)
                </button>
              </div>

              {/* API Public Tab */}
              {apiTab === 'public' && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Left Column - API Info */}
                  <div className="space-y-4">
                    {/* Base URL */}
                    <div className="bg-green-50 p-4 rounded-lg">
                      <h5 className="font-medium text-sm text-green-700 mb-2">🌐 Base URL</h5>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-white p-2 rounded border font-mono text-xs text-green-800">
                          {getApiUrls().public}
                        </div>
                        <button
                          onClick={() => copyToClipboard(getApiUrls().public, 'baseUrl')}
                          className="px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700"
                        >
                          {copiedField === 'baseUrl' ? '✓' : 'Copy'}
                        </button>
                      </div>
                      <p className="text-xs text-green-600 mt-1">API Public para integraciones externas</p>
                    </div>

                    {/* Authentication */}
                    <div className="bg-yellow-50 p-4 rounded-lg">
                      <h5 className="font-medium text-sm text-yellow-700 mb-2">🔑 Authentication</h5>
                      <p className="text-xs text-yellow-600 mb-2">Todas las llamadas requieren:</p>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-white p-2 rounded border font-mono text-xs text-yellow-800">
                          X-API-Key: {'<your_api_key>'}
                        </div>
                      </div>
                      <p className="text-xs text-yellow-600 mt-2">
                        Solicita tu API Key al administrador o créala desde el Dashboard.
                      </p>
                    </div>

                    {/* Endpoints */}
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h5 className="font-medium text-sm text-gray-700 mb-3">📡 Endpoints</h5>
                      <div className="space-y-2 text-xs font-mono">
                        <div className="flex items-start gap-2 p-2 bg-white rounded border">
                          <span className="text-green-600 font-bold w-12">POST</span>
                          <div>
                            <span className="text-gray-800">/simple/documents</span>
                            <p className="text-gray-500 font-sans mt-1">Crear documento (auto-calcula IGV/ISC)</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-2 p-2 bg-white rounded border">
                          <span className="text-blue-600 font-bold w-12">GET</span>
                          <div>
                            <span className="text-gray-800">/simple/documents</span>
                            <p className="text-gray-500 font-sans mt-1">Listar documentos</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-2 p-2 bg-white rounded border">
                          <span className="text-blue-600 font-bold w-12">GET</span>
                          <div>
                            <span className="text-gray-800">/simple/documents/{'{id}'}</span>
                            <p className="text-gray-500 font-sans mt-1">Obtener documento por ID</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-2 p-2 bg-white rounded border">
                          <span className="text-green-600 font-bold w-12">POST</span>
                          <div>
                            <span className="text-gray-800">/simple/documents/{'{id}'}/submit</span>
                            <p className="text-gray-500 font-sans mt-1">Enviar a SUNAT</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-2 p-2 bg-white rounded border">
                          <span className="text-red-600 font-bold w-12">POST</span>
                          <div>
                            <span className="text-gray-800">/simple/documents/{'{id}'}/void</span>
                            <p className="text-gray-500 font-sans mt-1">Anular documento</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-2 p-2 bg-white rounded border">
                          <span className="text-blue-600 font-bold w-12">GET</span>
                          <div>
                            <span className="text-gray-800">/customers/lookup?doc_type=6&doc_number=...</span>
                            <p className="text-gray-500 font-sans mt-1">Buscar cliente por RUC/DNI</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Documentation Link */}
                    <div className="bg-purple-50 p-4 rounded-lg">
                      <h5 className="font-medium text-sm text-purple-700 mb-2">📚 Documentación</h5>
                      <a
                        href={getApiUrls().swagger}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm"
                      >
                        <span>Abrir Swagger UI</span>
                        <span>↗</span>
                      </a>
                      <p className="text-xs text-purple-600 mt-2">
                        Documentación interactiva con ejemplos y pruebas
                      </p>
                    </div>
                  </div>

                  {/* Right Column - Code Example & Testing */}
                  <div className="space-y-4">
                    {/* Code Example */}
                    <div className="bg-gray-900 p-4 rounded-lg">
                      <div className="flex justify-between items-center mb-2">
                        <h5 className="font-medium text-sm text-gray-300">💻 Ejemplo cURL</h5>
                        <button
                          onClick={() => copyToClipboard(`curl -X POST ${getApiUrls().public}/simple/documents \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "document_type": "03",
    "customer": {
      "document_type": "1",
      "document_number": "12345678",
      "name": "CLIENTE EJEMPLO"
    },
    "items": [{
      "description": "Producto de prueba",
      "quantity": 1,
      "unit_price": 100.00
    }],
    "auto_submit": true
  }'`, 'curlExample')}
                          className="px-2 py-1 bg-gray-700 text-gray-300 rounded text-xs hover:bg-gray-600"
                        >
                          {copiedField === 'curlExample' ? '✓ Copied' : 'Copy'}
                        </button>
                      </div>
                      <pre className="text-xs text-green-400 overflow-x-auto whitespace-pre-wrap">
{`curl -X POST ${getApiUrls().public}/simple/documents \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "document_type": "03",
    "customer": {
      "document_type": "1",
      "document_number": "12345678",
      "name": "CLIENTE EJEMPLO"
    },
    "items": [{
      "description": "Producto",
      "quantity": 1,
      "unit_price": 100.00
    }],
    "auto_submit": true
  }'`}
                      </pre>
                    </div>

                    {/* Test Actions */}
                    <div className="space-y-3">
                      <h4 className="font-medium text-gray-700 border-b pb-2">🧪 Test Document Generation</h4>
                      <button
                        onClick={() => generateTestDocument(selectedTenant.id)}
                        disabled={testLoading}
                        className="w-full bg-green-600 text-white px-4 py-3 rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors text-sm font-medium"
                      >
                        {testLoading ? '⏳ Generating...' : '📄 Generate Test Invoice'}
                      </button>

                      <button
                        onClick={() => submitToSunat(selectedTenant.id)}
                        disabled={testLoading}
                        className="w-full bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors text-sm font-medium"
                      >
                        {testLoading ? '⏳ Submitting...' : '📤 Submit to SUNAT (Beta)'}
                      </button>

                      {testDocumentResult && (
                        <div className="bg-gray-50 p-4 rounded-lg border">
                          <h5 className="font-medium text-sm text-gray-700 mb-2">📋 Result</h5>
                          <pre className="text-xs text-gray-600 whitespace-pre-wrap bg-white p-3 rounded border overflow-auto max-h-32">
                            {testDocumentResult}
                          </pre>
                        </div>
                      )}
                    </div>

                    {/* Testing Notes */}
                    <div className="bg-orange-50 p-4 rounded-lg">
                      <h5 className="font-medium text-sm text-orange-700 mb-2">⚠️ Tenant Status</h5>
                      <ul className="text-xs text-orange-600 space-y-1">
                        <li>• Environment: <strong>{selectedTenant.environment}</strong></li>
                        <li>• SUNAT credentials: {selectedTenant.clave_sol_usuario ? '✅ Configured' : '❌ Missing'}</li>
                        <li>• Digital certificate: {selectedTenant.has_digital_certificate ? '✅ Assigned' : '❌ Not assigned'}</li>
                        {selectedTenant.has_digital_certificate && selectedTenant.certificate_ruc && (
                          <li>• Certificate RUC: {selectedTenant.certificate_ruc} {selectedTenant.certificate_ruc === selectedTenant.ruc ? '✅' : '⚠️'}</li>
                        )}
                      </ul>
                    </div>

                    {/* Allowed Documents */}
                    <div className="bg-green-50 p-4 rounded-lg">
                      <h5 className="font-medium text-sm text-green-700 mb-2">📊 Allowed Documents</h5>
                      <div className="flex flex-wrap gap-2">
                        {selectedTenant.allowed_document_types?.map((type) => {
                          const labels: Record<string, string> = {
                            '01': 'Factura',
                            '03': 'Boleta',
                            '07': 'Nota Crédito',
                            '08': 'Nota Débito',
                            '09': 'Guía Remisión'
                          };
                          return (
                            <span key={type} className="inline-flex items-center px-2 py-1 rounded text-xs bg-green-200 text-green-800">
                              {type}: {labels[type] || 'Unknown'}
                            </span>
                          );
                        }) || (
                          <span className="text-xs text-gray-500">No document types configured</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* API Gateway Tab */}
              {apiTab === 'gateway' && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Left Column */}
                  <div className="space-y-4">
                    {/* Base URL */}
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <h5 className="font-medium text-sm text-blue-700 mb-2">🌐 Base URL</h5>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-white p-2 rounded border font-mono text-xs text-blue-800">
                          {getApiUrls().gateway}
                        </div>
                        <button
                          onClick={() => copyToClipboard(getApiUrls().gateway, 'gatewayUrl')}
                          className="px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
                        >
                          {copiedField === 'gatewayUrl' ? '✓' : 'Copy'}
                        </button>
                      </div>
                      <p className="text-xs text-blue-600 mt-1">API Gateway con autenticación JWT</p>
                    </div>

                    {/* Authentication */}
                    <div className="bg-yellow-50 p-4 rounded-lg">
                      <h5 className="font-medium text-sm text-yellow-700 mb-2">🔐 Authentication</h5>
                      <p className="text-xs text-yellow-600 mb-2">Todas las llamadas requieren:</p>
                      <div className="space-y-2">
                        <div className="bg-white p-2 rounded border font-mono text-xs text-yellow-800">
                          Authorization: Bearer {'<jwt_token>'}
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-white p-2 rounded border font-mono text-xs text-yellow-800">
                            X-Tenant-ID: {selectedTenant.id}
                          </div>
                          <button
                            onClick={() => copyToClipboard(selectedTenant.id, 'tenantId')}
                            className="px-2 py-1 bg-yellow-600 text-white rounded text-xs hover:bg-yellow-700"
                          >
                            {copiedField === 'tenantId' ? '✓' : 'Copy'}
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Endpoints */}
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h5 className="font-medium text-sm text-gray-700 mb-3">📡 Endpoints</h5>
                      <div className="space-y-2 text-xs font-mono">
                        <div className="flex items-start gap-2 p-2 bg-white rounded border">
                          <span className="text-green-600 font-bold w-12">POST</span>
                          <div>
                            <span className="text-gray-800">/documents</span>
                            <p className="text-gray-500 font-sans mt-1">Crear documento (UBL completo)</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-2 p-2 bg-white rounded border">
                          <span className="text-blue-600 font-bold w-12">GET</span>
                          <div>
                            <span className="text-gray-800">/documents</span>
                            <p className="text-gray-500 font-sans mt-1">Listar documentos</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-2 p-2 bg-white rounded border">
                          <span className="text-green-600 font-bold w-12">POST</span>
                          <div>
                            <span className="text-gray-800">/sunat/submit</span>
                            <p className="text-gray-500 font-sans mt-1">Enviar a SUNAT</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-2 p-2 bg-white rounded border">
                          <span className="text-blue-600 font-bold w-12">GET</span>
                          <div>
                            <span className="text-gray-800">/sunat/status/{'{document_id}'}</span>
                            <p className="text-gray-500 font-sans mt-1">Consultar estado SUNAT</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right Column */}
                  <div className="space-y-4">
                    <div className="bg-orange-50 p-4 rounded-lg">
                      <h5 className="font-medium text-sm text-orange-700 mb-2">ℹ️ Nota</h5>
                      <p className="text-xs text-orange-600">
                        El API Gateway está diseñado para uso interno del Tenant Portal.
                        Para integraciones externas, se recomienda usar el <strong>API Public</strong> con API Keys.
                      </p>
                    </div>

                    {/* Testing Notes */}
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h5 className="font-medium text-sm text-gray-700 mb-2">⚠️ Tenant Status</h5>
                      <ul className="text-xs text-gray-600 space-y-1">
                        <li>• Environment: <strong>{selectedTenant.environment}</strong></li>
                        <li>• SUNAT credentials: {selectedTenant.clave_sol_usuario ? '✅ Configured' : '❌ Missing'}</li>
                        <li>• Digital certificate: {selectedTenant.has_digital_certificate ? '✅ Assigned' : '❌ Not assigned'}</li>
                      </ul>
                    </div>

                    {/* Allowed Documents */}
                    <div className="bg-green-50 p-4 rounded-lg">
                      <h5 className="font-medium text-sm text-green-700 mb-2">📊 Allowed Documents</h5>
                      <div className="flex flex-wrap gap-2">
                        {selectedTenant.allowed_document_types?.map((type) => {
                          const labels: Record<string, string> = {
                            '01': 'Factura',
                            '03': 'Boleta',
                            '07': 'Nota Crédito',
                            '08': 'Nota Débito',
                            '09': 'Guía Remisión'
                          };
                          return (
                            <span key={type} className="inline-flex items-center px-2 py-1 rounded text-xs bg-green-200 text-green-800">
                              {type}: {labels[type] || 'Unknown'}
                            </span>
                          );
                        }) || (
                          <span className="text-xs text-gray-500">No document types configured</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex items-center justify-end pt-6 border-t mt-6">
                <button
                  onClick={() => setShowApiModal(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TenantsManager;